/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks\useOpenVPNClient.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the openvpn-client module.
────────────────────────────────────────────────────────────────*/


import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../hooks/useNotifications';
import { useI18n } from '../../../hooks/useI18n';
import * as api from '../api/openvpnClientApi';
import type { OpenVPNConnection } from '../types';

export function useOpenVPNClient() {
    const user = useAuthStore((state) => state.user);
    const tenantSlug = user?.tenant?.slug;
    const { showSuccess, showError } = useNotifications();
    const { t } = useI18n();

    const [connection, setConnection] = useState<OpenVPNConnection | null>(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [connecting, setConnecting] = useState(false);

    // Load connection data
    const loadConnection = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.getConnection(tenantSlug, user?.id);
            if (response.success && response.data) {
                setConnection(response.data);
            }
        } catch (error) {
            console.error('Failed to load OpenVPN client connection:', error);
            showError(t('openvpnClient.messages.loadError', 'Failed to load connection status'));
        } finally {
            setLoading(false);
        }
    }, [tenantSlug, user?.id, showError, t]);

    // Initial load
    useEffect(() => {
        loadConnection();
    }, [loadConnection]);

    // Upload configuration
    const uploadConfig = useCallback(async (ovpnContent: string) => {
        setUploading(true);
        try {
            const response = await api.uploadConfig({ ovpnContent }, tenantSlug, user?.id);
            if (response.success && response.data) {
                setConnection(response.data);
                showSuccess(t('openvpnClient.messages.uploadSuccess', 'Configuration uploaded successfully'));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to upload config:', error);
            showError(error.message || t('openvpnClient.messages.uploadError', 'Failed to upload configuration'));
            return false;
        } finally {
            setUploading(false);
        }
    }, [tenantSlug, user?.id, showSuccess, showError, t]);

    // Connect
    const connect = useCallback(async () => {
        setConnecting(true);
        try {
            const response = await api.connect(tenantSlug, user?.id);
            if (response.success && response.data) {
                setConnection(response.data);
                showSuccess(t('openvpnClient.messages.connectSuccess', 'Connected successfully'));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to connect:', error);
            showError(error.message || t('openvpnClient.messages.connectError', 'Failed to connect'));
            return false;
        } finally {
            setConnecting(false);
        }
    }, [tenantSlug, user?.id, showSuccess, showError, t]);

    // Disconnect
    const disconnect = useCallback(async () => {
        setConnecting(true);
        try {
            const response = await api.disconnect(tenantSlug, user?.id);
            if (response.success && response.data) {
                setConnection(response.data);
                showSuccess(t('openvpnClient.messages.disconnectSuccess', 'Disconnected successfully'));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to disconnect:', error);
            showError(error.message || t('openvpnClient.messages.disconnectError', 'Failed to disconnect'));
            return false;
        } finally {
            setConnecting(false);
        }
    }, [tenantSlug, user?.id, showSuccess, showError, t]);

    // Delete configuration
    const deleteConfiguration = useCallback(async () => {
        try {
            const response = await api.deleteConfig(tenantSlug, user?.id);
            if (response.success) {
                await loadConnection();
                showSuccess(t('openvpnClient.messages.deleteSuccess', 'Configuration deleted successfully'));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to delete config:', error);
            showError(error.message || t('openvpnClient.messages.deleteError', 'Failed to delete configuration'));
            return false;
        }
    }, [tenantSlug, user?.id, loadConnection, showSuccess, showError, t]);

    // Refresh status
    const refreshStatus = useCallback(async () => {
        try {
            const response = await api.refreshStatus(tenantSlug, user?.id);
            if (response.success && response.data) {
                setConnection(response.data);
            }
        } catch (error) {
            console.error('Failed to refresh status:', error);
        }
    }, [tenantSlug, user?.id]);

    return {
        connection,
        loading,
        uploading,
        connecting,
        uploadConfig,
        connect,
        disconnect,
        deleteConfiguration,
        refreshStatus,
        reload: loadConnection
    };
}
